import { Box, Button, Fade, Stack, Typography } from '@mui/material'
import { forwardRef, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../../store/store'
import { UserActions, UserSelector } from '../../../../store/User'
import { WaterQualityIndexSelectors } from '../../../../store/waterQualityIndex'
import { ProductType } from '../../../../utils/productTypeWaitlist'
import { SippProValueCard } from './SippProValueCard'
import { Step1PlanChoice } from './Step1PlanChoice'
import { Step2EquipmentPicker } from './Step2EquipmentPicker'
import { Step3RefrigeratorBranch } from './Step3RefrigeratorBranch'
import { Step3SmartHomeBranch } from './Step3SmartHomeBranch'
import { Step3WaitlistBranch } from './Step3WaitlistBranch'

export type PlanChoice = 'free' | 'pro' | null
export type EquipmentChoice =
  | 'refrigerator'
  | 'under_sink'
  | 'pitcher'
  | 'countertop'
  | 'whole_home'
  | 'smart_home'
  | null

const EQUIPMENT_LABEL: Record<Exclude<EquipmentChoice, null>, string> = {
  refrigerator: 'refrigerator filter',
  under_sink: 'under-sink system',
  pitcher: 'water pitcher',
  countertop: 'countertop filter',
  whole_home: 'whole-home system',
  smart_home: 'smart home monitor',
}

// Title-case display labels for the Step 2 summary row that appears once
// the user has committed to an equipment choice and Step 3 is active.
const EQUIPMENT_DISPLAY_LABEL: Record<Exclude<EquipmentChoice, null>, string> = {
  refrigerator: 'Refrigerator Filter',
  under_sink: 'Under-Sink System',
  pitcher: 'Water Pitcher',
  countertop: 'Countertop Filter',
  whole_home: 'Whole-Home System',
  smart_home: 'Smart Home Monitor',
}

// Shared style for the "Step N" summary card the wizard collapses into
// once that step is resolved.
const SUMMARY_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
} as const

const CHANGE_LINK_SX = {
  cursor: 'pointer',
  textDecoration: 'underline',
} as const

const EQUIPMENT_TO_WAITLIST_PRODUCT: Partial<
  Record<Exclude<EquipmentChoice, null>, ProductType>
> = {
  under_sink: 'under_sink_system',
  pitcher: 'water_pitcher',
  countertop: 'counter_top_filter',
  whole_home: 'whole_home_system',
}

const STEP_LABEL_SX = {
  typography: 'overline',
  color: 'text.secondary',
  letterSpacing: 1,
} as const

export const PlanSelectionSection = forwardRef<HTMLDivElement>((_props, ref) => {
  const dispatch = useDispatch<AppDispatch>()
  const user = useSelector(UserSelector.user)
  const wqiOverall = useSelector(WaterQualityIndexSelectors.selectOverall)
  const waterSafetyStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)

  // Wizard intentionally starts with no plan chosen so Step 2 stays hidden
  // until the user makes an active choice. SIPP Pro is still the visually
  // recommended card (handled in `Step1PlanChoice`).
  const [planChoice, setPlanChoice] = useState<PlanChoice>(null)
  const [equipmentChoice, setEquipmentChoice] = useState<EquipmentChoice>(null)
  // SIPP-844: when the user opts into Free, collapse the entire stepper
  // (including the value-prop card) into a compact confirmation card. Lets
  // users back into the upgrade flow via "Upgrade anytime →".
  const [freePlanConfirmed, setFreePlanConfirmed] = useState(false)

  // SIPP-825: Plan is the entry point of the wizard. Whenever the user moves
  // off of Pro (flipping back to Free or clearing the selection), drop any
  // downstream equipment + Step 3 state so Step 2/3 don't resurrect with
  // zombie selections the next time they click Choose SIPP Pro.
  // SIPP-844: Free selection also collapses the section + fires CIO event.
  const handlePlanChoice = useCallback(
    (next: NonNullable<PlanChoice>) => {
      if (next !== 'pro') {
        setEquipmentChoice(null)
      }
      setPlanChoice(next)
      if (next === 'free' && user?._id) {
        setFreePlanConfirmed(true)
        dispatch(
          UserActions.fireFreePlanSelectedAsync({
            userId: user._id,
            wqiScore: wqiOverall?.score ?? null,
            waterSafetyStatus,
          })
        )
      }
    },
    [dispatch, user?._id, wqiOverall?.score, waterSafetyStatus]
  )

  // SIPP-837: stable callback so the `useEffect` inside Step2EquipmentPicker
  // — which has `onSelect` in its dependency array — only re-fires when the
  // user actually changes equipment. Passing `setEquipmentChoice` directly
  // produced a new function reference every parent render, which let the
  // pre-select effect overwrite a fresh user click with the latest
  // `preSelectedKey` and made Step 3 fail to reveal.
  const handleEquipmentChoice = useCallback(
    (next: NonNullable<EquipmentChoice>) => setEquipmentChoice(next),
    []
  )

  // SIPP-844: re-expand the section when the user wants to reconsider Pro.
  const handleReopenPlanFlow = useCallback(() => {
    setFreePlanConfirmed(false)
    setPlanChoice(null)
  }, [])

  const showStep2 = planChoice === 'pro'
  const showStep3 = showStep2 && equipmentChoice !== null

  if (freePlanConfirmed) {
    return (
      <Box ref={ref} sx={{ scrollMarginTop: 80 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body1" fontWeight={600}>
              You&apos;re on the free plan — your results are saved.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can access your report anytime by logging back in.
            </Typography>
          </Stack>
          <Button variant="outlined" size="small" onClick={handleReopenPlanFlow}>
            Upgrade anytime →
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box ref={ref} sx={{ scrollMarginTop: 80 }}>
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant="h4">Unlock your full protection plan.</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose your tier, then build the system that fits your home.
          </Typography>
        </Stack>

        {/* SIPP-843: 3 Cs value prop card sits above Step 1 — frames the
            decision before the user picks a plan. Always visible. */}
        <SippProValueCard />

        {/* Step 1 — full plan cards while the wizard is at the entry point;
            collapses to a summary row once the user has committed (and
            Step 2 has expanded). The Free path collapses the entire section
            via `freePlanConfirmed` upstream, so this branch only ever
            renders the summary for an active Pro choice. */}
        {planChoice === null && (
          <Stack spacing={1.5}>
            <Typography sx={STEP_LABEL_SX}>STEP 1 — CHOOSE YOUR PLAN</Typography>
            <Step1PlanChoice planChoice={planChoice} onSelect={handlePlanChoice} />
          </Stack>
        )}

        {planChoice !== null && !freePlanConfirmed && (
          <Box sx={SUMMARY_ROW_SX}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                STEP 1
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {planChoice === 'pro' ? 'SIPP Pro — $99/year' : 'Free plan'}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="primary.dark"
              sx={CHANGE_LINK_SX}
              onClick={() => {
                setPlanChoice(null)
                setEquipmentChoice(null)
              }}
            >
              Change
            </Typography>
          </Box>
        )}

        {/* Step 2 — full equipment picker until the user picks a tile, then
            collapses to a summary row so Step 3 has the floor. */}
        {showStep2 && !showStep3 && (
          <Fade in unmountOnExit>
            <Stack spacing={1.5}>
              <Typography sx={STEP_LABEL_SX}>
                STEP 2 — YOUR PRIMARY WATER EQUIPMENT
              </Typography>
              {/* SIPP-828: subtext lives inside Step2EquipmentPicker so it can
                  swap copy for users who answered "None / I don't use a filter"
                  in the test strip flow. */}
              <Step2EquipmentPicker
                equipmentChoice={equipmentChoice}
                onSelect={handleEquipmentChoice}
              />
            </Stack>
          </Fade>
        )}

        {showStep2 && showStep3 && (
          <Box sx={SUMMARY_ROW_SX}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                STEP 2
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {equipmentChoice
                  ? EQUIPMENT_DISPLAY_LABEL[equipmentChoice]
                  : ''}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="primary.dark"
              sx={CHANGE_LINK_SX}
              onClick={() => setEquipmentChoice(null)}
            >
              Change
            </Typography>
          </Box>
        )}

        <Fade in={showStep3} unmountOnExit>
          <Stack spacing={1.5}>
            {equipmentChoice === 'refrigerator' && (
              <>
                <Typography sx={STEP_LABEL_SX}>
                  STEP 3 — LET&apos;S FIND YOUR FILTER
                </Typography>
                <Step3RefrigeratorBranch />
              </>
            )}
            {equipmentChoice === 'smart_home' && (
              <>
                <Typography sx={STEP_LABEL_SX}>STEP 3 — HOW MANY UNITS?</Typography>
                <Step3SmartHomeBranch />
              </>
            )}
            {equipmentChoice &&
              equipmentChoice !== 'refrigerator' &&
              equipmentChoice !== 'smart_home' && (
                <>
                  <Typography sx={STEP_LABEL_SX}>STEP 3 — JOIN THE WAITLIST</Typography>
                  <Step3WaitlistBranch
                    equipmentType={EQUIPMENT_LABEL[equipmentChoice]}
                    productType={
                      EQUIPMENT_TO_WAITLIST_PRODUCT[equipmentChoice] ?? 'water_pitcher'
                    }
                  />
                </>
              )}
          </Stack>
        </Fade>
      </Stack>
    </Box>
  )
})

PlanSelectionSection.displayName = 'PlanSelectionSection'
